import { create } from "zustand";
import { fetchEntitlements, type EntitlementSnapshot } from "./entitlements.api";

interface EntitlementState {
  ownerUserId: string | null;
  snapshot: EntitlementSnapshot | null;
  loading: boolean;
  loadError: boolean;
  load: (userId: string | null) => Promise<void>;
  refresh: () => Promise<void>;
}

const inFlight = new Map<string, Promise<void>>();
const queuedRefresh = new Set<string>();
export const useEntitlementsStore = create<EntitlementState>((set, get) => {
  const request = async (refresh = false): Promise<void> => {
    const ownerUserId = get().ownerUserId;
    if (!ownerUserId) return;
    const existing = inFlight.get(ownerUserId);
    if (existing) {
      if (refresh) queuedRefresh.add(ownerUserId);
      return existing;
    }
    const pending = fetchEntitlements()
      .then((snapshot) => {
        if (get().ownerUserId === ownerUserId) set({ snapshot, loading: false, loadError: false });
      })
      .catch(() => {
        if (get().ownerUserId === ownerUserId) set({ loading: false, loadError: true });
      })
      .finally(() => {
        inFlight.delete(ownerUserId);
        if (queuedRefresh.delete(ownerUserId) && get().ownerUserId === ownerUserId) return request();
      });
    inFlight.set(ownerUserId, pending);
    return pending;
  };
  return {
    ownerUserId: null,
    snapshot: null,
    loading: true,
    loadError: false,
    load: async (userId) => {
      if (!userId) {
        set({ ownerUserId: null, snapshot: null, loading: false, loadError: false });
        return;
      }
      if (get().ownerUserId === userId && get().snapshot) return;
      set({ ownerUserId: userId, snapshot: null, loading: true, loadError: false });
      await request();
    },
    refresh: () => request(true),
  };
});
